/**
 * Map Theme Service
 *
 * Handles Google Maps styling and InfoWindow theming.
 * Extracts theme-specific styling logic from the map canvas component
 * to maintain separation of concerns.
 *
 * @module map-theme-service
 */

/// <reference types="@types/google.maps" />

import { getIconHtml } from "@/features/ui/icon";

export type ThemeName = "dark" | "warm" | "default";

/**
 * Get the current theme name based on document classes
 */
export function getCurrentTheme(): ThemeName {
  const classes = document.documentElement.classList;
  if (classes.contains("theme-dark")) return "dark";
  if (classes.contains("theme-warm")) return "warm";
  return "default";
}

/**
 * Get CSS variable value from computed styles
 */
export function getCSSVariable(variableName: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
}

/**
 * Apply custom styling to Google Maps InfoWindow DOM elements.
 *
 * Google Maps InfoWindow API does not provide direct styling options.
 * This function manipulates the DOM after the InfoWindow renders
 * to apply theme-appropriate styling to the container, tail, and close button.
 *
 * @param theme - Current theme name
 * @param surfaceColor - Background color for the InfoWindow
 */
export function applyInfoWindowStyling(
  theme: ThemeName,
  surfaceColor: string
): void {
  const iwOuter = document.querySelector(".gm-style-iw-c");
  const iwBackground = document.querySelector(".gm-style-iw-d");
  const iwTail = document.querySelector(".gm-style-iw-tc");
  const iwCloseContainer = document.querySelector(".gm-style-iw-chr");
  const closeButton = document.querySelector(".gm-ui-hover-effect");

  if (iwOuter) {
    // Apply themed background to outer container (single source of truth for background)
    (iwOuter as HTMLElement).style.background = surfaceColor;
    (iwOuter as HTMLElement).style.padding = "0";
    (iwOuter as HTMLElement).style.overflow = "visible";
    (iwOuter as HTMLElement).style.boxShadow = "none";
  }

  if (iwBackground) {
    // Remove white background from inner container
    (iwBackground as HTMLElement).style.background = "transparent";
    (iwBackground as HTMLElement).style.overflow = "visible";
  }

  // Style the close button container (gm-style-iw-chr)
  // This container holds the close button and affects its positioning
  if (iwCloseContainer) {
    const container = iwCloseContainer as HTMLElement;
    // Position the close button container in the top-right of the tooltip
    container.style.position = "absolute";
    container.style.top = "8px";
    container.style.right = "8px";
    container.style.zIndex = "1000"; // High z-index to ensure it appears above tooltip content
    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
  }

  // Style the close button based on current theme
  if (closeButton) {
    const button = closeButton as HTMLElement;

    // Reset button positioning (it's now positioned by the container)
    button.style.position = "relative";
    button.style.top = "0";
    button.style.right = "0";
    button.style.width = "24px";
    button.style.height = "24px";
    button.style.borderRadius = "50%";
    button.style.display = "flex";
    button.style.alignItems = "center";
    button.style.justifyContent = "center";
    button.style.opacity = "1";
    button.style.cursor = "pointer";

    // Google Maps native close button icon is unreliable across versions.
    // Replace button contents with a guaranteed-visible SVG X icon.
    button.style.backgroundColor = theme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)";
    button.style.color = theme === "dark" ? "#ffffff" : "#333333";
    button.innerHTML = getIconHtml("x-mark", "display:block;pointer-events:none;width:16px;height:16px;");
  }

  // Style the triangle pointer (tail) based on current theme
  if (iwTail) {
    const tail = iwTail as HTMLElement;

    // Make the tail visible
    tail.style.display = "block";

    // The tail structure:
    // .gm-style-iw-tc contains a child div that has ::after pseudo-element
    // The ::after creates the actual triangle via border styling
    const tailInner = tail.querySelector("div");
    if (tailInner) {
      const innerEl = tailInner as HTMLElement;

      // The inner div also has a white background that creates the triangle effect
      // We need to override this with our theme color
      innerEl.style.background = surfaceColor;

      // The actual triangle is created via ::after with transparent borders
      // We need to inject a style to override the ::after pseudo-element
      // Since we can't directly style ::after, we use a CSS injection approach
      const styleId = "infowindow-tail-style";
      let styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }

      // Create the tail triangle with theme-appropriate colors
      // The tail is a triangle pointing down, created with borders
      styleEl.textContent = `
        .gm-style-iw-tc::after {
          background: ${surfaceColor} !important;
        }
        .gm-style-iw-tc > div {
          background: ${surfaceColor} !important;
        }
        .gm-style-iw-tc > div::after {
          background: ${surfaceColor} !important;
          box-shadow: none !important;
        }
      `;
    }
  }
}

/**
 * Get Google Maps styles for the specified theme.
 *
 * Returns an array of MapTypeStyle objects that customize the map appearance
 * to match the application's theme system.
 *
 * @param theme - Theme name to get styles for
 * @returns Array of Google Maps MapTypeStyle objects
 */
export function getMapStyles(theme: ThemeName): google.maps.MapTypeStyle[] {
  // Use Google's default map theme with no custom styling
  return [];

  // Common styles for all themes (hide POI and transit labels)
  const commonStyles: google.maps.MapTypeStyle[] = [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ];

  // Dark theme map styles
  if (theme === "dark") {
    return [
      ...commonStyles,
      // Base geometry
      {
        elementType: "geometry",
        stylers: [{ color: "#1a1f26" }], // Match --color-surface
      },
      // Water styling
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#0f1419" }], // Match --color-bg
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6b7280" }], // Match --color-text-subtle
      },
      {
        featureType: "water",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#0f1419" }, { weight: 2 }],
      },
      // Road styling - base geometry
      {
        featureType: "road",
        elementType: "geometry.fill",
        stylers: [{ color: "#2d3540" }], // Match --color-surface-subtle
      },
      {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#1a1f26" }, { weight: 1 }], // Subtle outline
      },
      // Road labels - all types
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca3af" }], // Match --color-text-muted
      },
      {
        featureType: "road",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1a1f26" }, { weight: 3 }], // Dark outline for readability
      },
      // Highway-specific styling
      {
        featureType: "road.highway",
        elementType: "geometry.fill",
        stylers: [{ color: "#3d4451" }], // Match --color-border
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#242a33" }, { weight: 2 }],
      },
      {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{ color: "#e6e8eb" }], // Brighter for highways
      },
      // Arterial roads
      {
        featureType: "road.arterial",
        elementType: "geometry.fill",
        stylers: [{ color: "#2d3540" }],
      },
      {
        featureType: "road.arterial",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca3af" }],
      },
      // Local roads
      {
        featureType: "road.local",
        elementType: "geometry.fill",
        stylers: [{ color: "#242a33" }],
      },
      {
        featureType: "road.local",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6b7280" }], // Subtler for local roads
      },
      // Administrative labels (cities, regions)
      {
        featureType: "administrative",
        elementType: "labels.text.fill",
        stylers: [{ color: "#e6e8eb" }], // Match --color-text
      },
      {
        featureType: "administrative",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1a1f26" }, { weight: 3 }],
      },
      {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#e6e8eb" }], // City names bright
      },
      {
        featureType: "administrative.neighborhood",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca3af" }], // Neighborhoods muted
      },
      // Landscape
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#242a33" }], // Match --color-surface-muted
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#1a1f26" }],
      },
      // POI - Parks (show with muted styling)
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#1a2d23" }], // Dark green tint
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6ee7b7" }], // Match --color-success-text for green
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1a1f26" }, { weight: 2 }],
      },
    ];
  }

  // Warm theme map styles
  if (theme === "warm") {
    return [
      ...commonStyles,
      {
        elementType: "geometry",
        stylers: [{ color: "#f3e8d8" }], // Match --color-surface-subtle (sand-100)
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#d4e4e8" }], // Muted blue-gray for water
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#6b6259" }], // Match --color-interactive (stone-600)
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#ffffff" }], // White roads
      },
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#8b8073" }], // Match --color-text-muted (stone-500)
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#fff5f0" }], // Cream highways
      },
      {
        featureType: "administrative",
        elementType: "labels.text.fill",
        stylers: [{ color: "#183028" }], // Match --color-text (forest)
      },
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#fbf4ea" }], // Match --color-surface-muted (sand-50)
      },
    ];
  }

  // Default/cool theme - minimal styling
  return [
    ...commonStyles,
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#c9d9e8" }], // Subtle blue water
    },
    {
      featureType: "landscape",
      elementType: "geometry",
      stylers: [{ color: "#f3f4f6" }], // Match --color-surface-subtle
    },
  ];
}
